import { SurveyLogicElement } from '@vision/ui/interfaces/survey-logic-builder';
import { getBodyText } from '@vision/ui/utils/data.utils';

export interface SelectOption {
  value: string;
  label: string;
}

export function getLabel(
  body: Record<string, string> | undefined,
  activeLanguageCode: string,
  fallback: string,
): string {
  if (!body || typeof body !== 'object') return fallback;
  return getBodyText(body, activeLanguageCode) || fallback;
}

export function getElementLabel(
  elements: SurveyLogicElement[],
  elementKey: string,
  activeLanguageCode: string,
): string {
  const element = elements.find((candidate) => candidate.key === elementKey);
  return element ? getLabel(element.detail?.body || element.body, activeLanguageCode, elementKey) : elementKey;
}

export function buildElementOptions(elements: SurveyLogicElement[], activeLanguageCode: string): SelectOption[] {
  if (!Array.isArray(elements)) return [];

  return elements
    .filter((el) => el?.key && (el.detail?.body || el.body))
    .map((el) => ({
      value: el.key,
      label: getLabel(el.detail?.body || el.body, activeLanguageCode, el.key),
    }))
    .filter((item) => item.value && item.label);
}

export function buildElementOptionsWithOptions(
  elements: SurveyLogicElement[],
  activeLanguageCode: string,
): SelectOption[] {
  if (!Array.isArray(elements)) return [];

  return elements
    .filter(
      (el) =>
        el?.key && (el.detail?.body || el.body) && Array.isArray(el.detail?.options) && el.detail.options.length > 0,
    )
    .map((el) => ({
      value: el.key,
      label: getLabel(el.detail?.body || el.body, activeLanguageCode, el.key),
    }))
    .filter((item) => item.value && item.label);
}

export function buildOptionOptions(
  selectedElement: SurveyLogicElement | undefined,
  activeLanguageCode: string,
): SelectOption[] {
  if (!selectedElement?.detail?.options) return [];

  return selectedElement.detail.options
    .filter((opt) => opt?.key)
    .map((opt) => ({
      value: opt.key,
      label: getLabel(opt.body, activeLanguageCode, opt.key),
    }))
    .filter((item) => item.value && item.label);
}

export function buildStatementOptions(
  selectedElement: SurveyLogicElement | undefined,
  activeLanguageCode: string,
): SelectOption[] {
  if (!selectedElement?.detail?.statements) return [];

  return selectedElement.detail.statements
    .filter((stmt) => stmt?.key)
    .map((stmt) => ({
      value: stmt.key,
      label: getLabel(stmt.body, activeLanguageCode, stmt.key),
    }))
    .filter((item) => item.value && item.label);
}

export function resolveOptionKey(
  selectedElement: SurveyLogicElement | undefined,
  activeLanguageCode: string,
  keyOrLabel: string,
): string | null {
  if (!selectedElement?.detail?.options) return null;

  const byKey = selectedElement.detail.options.find((opt) => opt.key === keyOrLabel);
  if (byKey) return byKey.key;

  const byLabel = selectedElement.detail.options.find((opt) => {
    const label = getLabel(opt.body, activeLanguageCode, opt.key);
    return label === keyOrLabel;
  });
  if (byLabel) return byLabel.key;

  return null;
}

export function resolveOptionKeyForElement(
  elements: SurveyLogicElement[],
  questionKey: string,
  activeLanguageCode: string,
  keyOrLabel: string,
): string | null {
  if (!Array.isArray(elements)) return null;

  const element = elements.find((el) => el.key === questionKey);
  return resolveOptionKey(element, activeLanguageCode, keyOrLabel);
}
